import { faCircleMinus, faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useInterval, useLatest } from 'react-use';
import styled, { css, keyframes } from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { ScoreTimeline } from '@sorare/core/src/components/scoring/ScoreTimeline';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const Wrapper = styled(Vertical)`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const Img = styled.img`
  max-width: 50%;
  object-fit: contain;
  margin-top: var(--double-unit);
  height: clamp(100px, 33vh, 330px);
`;

const TimelineWrapper = styled.div`
  padding-bottom: var(--triple-unit);
  position: relative;
  max-width: calc(100% + var(--quadruple-unit));
  margin: 0 calc(-1 * var(--double-unit));

  &:after {
    content: '';
    inset: 0;
    position: absolute;
    background: linear-gradient(90deg, #101010 0%, rgba(16, 16, 16, 0) 24%),
      linear-gradient(270deg, #101010 0%, rgba(16, 16, 16, 0) 24%);
  }
`;

const scaleIn = keyframes`
 50% {
    scale: 1.5;
  }
  100% {
    opacity: 1;
    scale: 1;
  }
`;

const ChipWrapper = styled.div<{ visible: boolean }>`
  opacity: 0;
  ${({ visible }) =>
    visible &&
    css`
      animation: ${fadeIn} 0.5s 0.3s forwards;
    `}
`;

const ScoreWrapper = styled.div<{ visible: boolean }>`
  opacity: 0;
  scale: 0.6;
  ${({ visible }) =>
    visible &&
    css`
      animation: ${scaleIn} 0.5s 0.3s forwards;
    `}
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: auto;
  padding: var(--double-unit);
`;

const TextContent = styled(Vertical)`
  display: flex;
  position: relative;
  align-items: center;
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  }
`;

const StyledScoreTimeline = styled(ScoreTimeline)`
  padding-left: 50%;
  overflow: visible;
`;

const labelStyle = css`
  display: flex;
  font-weight: var(--t-bold);
`;

const SuccessSpan = styled.span`
  ${labelStyle}
  color: var(--c-score-high);
`;
const FailureSpan = styled.span`
  ${labelStyle}
  color: var(--c-red-400);
`;
const plusIcon = (
  <FontAwesomeIcon icon={faCirclePlus} color="var(--c-score-high)" />
);

const minusIcon = (
  <FontAwesomeIcon icon={faCircleMinus} color="var(--c-red-400)" />
);
const NBA_TIMELINE = [
  {
    minute: 10,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.points"
        defaultMessage="25x Points"
      />
    ),
    points: 25,
    icon: plusIcon,
    quarter: 'Q1\u00A09:39',
  },
  {
    minute: 20,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.turnovers"
        defaultMessage="3x Turnovers"
      />
    ),
    points: -6,
    icon: minusIcon,
    quarter: 'Q2\u00A01:45',
  },
  {
    minute: 30,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.block"
        defaultMessage="3x Blocks"
      />
    ),
    points: 9,
    icon: plusIcon,
    quarter: 'Q2\u00A04:52',
  },
  {
    minute: 45,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.assist"
        defaultMessage="4x Assists"
      />
    ),
    points: 6,
    icon: plusIcon,
    quarter: 'Q3\u00A01:26',
  },
  {
    minute: 65,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.block"
        defaultMessage="3x Blocks"
      />
    ),
    points: 9,
    icon: plusIcon,
    quarter: 'Q3\u00A07:52',
  },
  {
    minute: 75,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.points2"
        defaultMessage="15x Points"
      />
    ),
    points: 15,
    icon: plusIcon,
    quarter: 'Q4\u00A08:13',
  },
  {
    minute: 85,
    label: (
      <FormattedMessage
        id="NBAScorePointsStep.steals"
        defaultMessage="2x Steals"
      />
    ),
    points: 6,
    icon: plusIcon,
    quarter: 'Q4\u00A010:13',
  },
];

const LabelText = styled.span`
  text-transform: capitalize;
`;

const LabelContent = ({
  label,
  points,
}: {
  label: ReactNode;
  points?: number;
}) => {
  const pointsLabel = (
    <FormattedMessage
      {...fantasy.pointsWithStyledScoreWithoutFormat}
      values={{
        score: points,
        span: chunks => <span>{chunks}</span>,
      }}
    />
  );

  const SpanComponent = points && points > 0 ? SuccessSpan : FailureSpan;

  return (
    <Horizontal center as="span">
      <LabelText>{label}</LabelText>
      {!!points && <SpanComponent>{pointsLabel}</SpanComponent>}
    </Horizontal>
  );
};

type Props = {
  children?: ReactNode;
  playerPictureUrl: string;
  playerImageAltText: string;
};
export const NBAScorePoints = ({
  children,
  playerPictureUrl,
  playerImageAltText,
}: Props) => {
  const [loopCount, setLoopCount] = useState(0);
  const [currentActionIndex, setCurrentActionIndex] = useState<number>();

  const latestActionIndex = useLatest(currentActionIndex);
  useInterval(() => {
    if ((latestActionIndex.current || 0) + 1 === NBA_TIMELINE.length) {
      setLoopCount(v => v + 1);
    }
    setCurrentActionIndex(v => {
      if (typeof v === 'undefined') {
        return 0;
      }
      if (v + 1 === NBA_TIMELINE.length) {
        return undefined;
      }
      return v + 1;
    });
  }, 2000);
  const lastAction =
    typeof currentActionIndex === 'undefined'
      ? null
      : NBA_TIMELINE.at(currentActionIndex)!;

  const score = NBA_TIMELINE.reduce((acc, { points }, index) => {
    if (
      typeof currentActionIndex === 'undefined' ||
      index > currentActionIndex
    ) {
      return acc;
    }
    return acc + points;
  }, 0);

  return (
    <>
      <TextContent gap={0.5}>
        <BrandM className="text-center">
          <FormattedMessage
            id="NBAScorePointsStep.title"
            defaultMessage="Score Points"
          />
        </BrandM>
        <BodyM color="var(--c-nd-600)" className="text-center">
          <FormattedMessage
            id="NBAScorePointsStep.subtitle"
            defaultMessage="Players gain fantasy points based on real life performances"
          />
        </BodyM>
      </TextContent>
      <Wrapper gap={4}>
        <Img src={playerPictureUrl} alt={playerImageAltText} />
        <Vertical gap={3} center>
          <ScoreWrapper visible>
            <Score
              // avoid animation on loop reset
              key={loopCount}
              score={score}
              animationDelayMs={0}
              scoreStatus={ScoreStatus.FINAL}
              withSuffix
            />
          </ScoreWrapper>
          <ChipWrapper visible>
            {lastAction ? (
              <Chip>
                <LabelContent
                  label={lastAction.label}
                  points={lastAction.points}
                />
              </Chip>
            ) : (
              <Chip>
                <LabelContent
                  label={
                    <FormattedMessage
                      id="NBA.ComposeTeamTutorial.starter"
                      defaultMessage="Game Start"
                    />
                  }
                />
              </Chip>
            )}
          </ChipWrapper>
          <TimelineWrapper>
            <StyledScoreTimeline
              timeline={NBA_TIMELINE}
              selectedMinute={lastAction?.minute || 0}
            />
          </TimelineWrapper>
        </Vertical>
      </Wrapper>
      <ButtonWrapper>{children}</ButtonWrapper>
    </>
  );
};
